import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import api from './api';

const KATEGORI_ETIKETLER = {
  yukseltme_talebi: { label: 'Hesap Yükseltme', renk: 'warning', icon: 'upgrade' },
  teknik_sorun: { label: 'Teknik Sorun', renk: 'danger', icon: 'bug_report' },
  fatura: { label: 'Fatura', renk: 'info', icon: 'receipt' },
  diger: { label: 'Diğer', renk: 'secondary', icon: 'help' },
};

const DURUM_ETIKETLER = {
  acik: { label: 'Açık', renk: 'success' },
  beklemede: { label: 'Beklemede', renk: 'warning' },
  cozuldu: { label: 'Çözüldü', renk: 'primary' },
  reddedildi: { label: 'Reddedildi', renk: 'danger' },
};

function DestekSayfasi({ theme }) {
  const navigate = useNavigate();
  const [ticketlar, setTicketlar] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);
  const [kullaniciBilgi, setKullaniciBilgi] = useState(null);

  // Yeni ticket modal state
  const [modalAcik, setModalAcik] = useState(false);
  const [yeniTicket, setYeniTicket] = useState({ baslik: '', kategori: 'diger', ilk_mesaj: '' });
  const [gonderiyor, setGonderiyor] = useState(false);
  const [modalHata, setModalHata] = useState(null);

  const fetchTicketlar = useCallback(async () => {
    try {
      const res = await api.get('/diyetisyen/destek/ticketlar');
      setTicketlar(res.data);
    } catch (e) {
      setHata('Ticket\'lar yüklenirken hata oluştu.');
    } finally {
      setYukleniyor(false);
    }
  }, []);

  const fetchKullaniciPano = useCallback(async () => {
    try {
      const res = await api.get('/diyetisyen/pano');
      setKullaniciBilgi(res.data);
    } catch {
      // pano yoksa devam et
    }
  }, []);

  useEffect(() => {
    fetchTicketlar();
    fetchKullaniciPano();
  }, [fetchTicketlar, fetchKullaniciPano]);

  const handleModalAc = (kategoriOneri = 'diger') => {
    setYeniTicket({ baslik: '', kategori: kategoriOneri, ilk_mesaj: '' });
    setModalHata(null);
    setModalAcik(true);
  };

  const handleTicketGonder = async (e) => {
    e.preventDefault();
    setGonderiyor(true);
    setModalHata(null);
    try {
      const res = await api.post('/diyetisyen/destek/ticket', yeniTicket);
      setModalAcik(false);
      setTicketlar(prev => [res.data.ticket, ...prev]);
      navigate(`/destek/ticket/${res.data.ticket.id}`);
    } catch (err) {
      const msg = err.response?.data?.mesaj || 'Ticket oluşturulurken hata oluştu.';
      setModalHata(msg);
    } finally {
      setGonderiyor(false);
    }
  };

  const isDemo = kullaniciBilgi?.is_demo;
  const bekleyenYukseltme = ticketlar.some(
    t => t.kategori === 'yukseltme_talebi' && ['acik', 'beklemede'].includes(t.durum)
  );

  const isDark = theme === 'dark';
  const cardClass = isDark ? 'bg-dark text-white border-secondary' : '';

  return (
    <Container fluid className="py-4 px-3 px-md-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <h4 className="fw-bold mb-0">
            <span className="material-symbols-outlined align-middle me-2" style={{ fontSize: '1.6rem' }}>headset_mic</span>
            Destek Talepleri
          </h4>
          <p className="text-muted small mt-1 mb-0">Sorunlarınızı bildirin, hesap yükseltme talebi oluşturun.</p>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={() => handleModalAc()}>
            <span className="material-symbols-outlined align-middle me-1" style={{ fontSize: '1rem' }}>add</span>
            Yeni Talep
          </Button>
        </Col>
      </Row>

      {/* Demo hesap yükseltme banner */}
      {isDemo && !bekleyenYukseltme && (
        <Alert variant="warning" className="d-flex align-items-center gap-3 mb-4">
          <span className="material-symbols-outlined" style={{ fontSize: '1.8rem' }}>upgrade</span>
          <div className="flex-grow-1">
            <strong>Demo hesabınızı yükseltin!</strong>
            <p className="mb-0 small">Tüm özelliklere sınırsız erişmek için hesap yükseltme talebi oluşturabilirsiniz.</p>
          </div>
          <Button variant="warning" size="sm" className="text-dark fw-bold" onClick={() => handleModalAc('yukseltme_talebi')}>
            Talep Oluştur
          </Button>
        </Alert>
      )}

      {isDemo && bekleyenYukseltme && (
        <Alert variant="info" className="d-flex align-items-center gap-3 mb-4">
          <span className="material-symbols-outlined" style={{ fontSize: '1.8rem' }}>hourglass_top</span>
          <div>
            <strong>Yükseltme talebiniz değerlendiriliyor.</strong>
            <p className="mb-0 small">Admin ekibimiz talebinizi inceliyor. Gelişmeleri aşağıdaki listeden takip edebilirsiniz.</p>
          </div>
        </Alert>
      )}

      {/* Ticket listesi */}
      {yukleniyor ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : hata ? (
        <Alert variant="danger">{hata}</Alert>
      ) : ticketlar.length === 0 ? (
        <Card className={`text-center py-5 ${cardClass}`}>
          <Card.Body>
            <span className="material-symbols-outlined d-block text-muted mb-3" style={{ fontSize: '3rem' }}>inbox</span>
            <p className="text-muted mb-3">Henüz bir destek talebiniz yok.</p>
            <Button variant="outline-primary" onClick={() => handleModalAc()}>
              İlk talebinizi oluşturun
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Card className={cardClass}>
          <Card.Body className="p-0">
            <div className="list-group list-group-flush">
              {ticketlar.map(ticket => {
                const kat = KATEGORI_ETIKETLER[ticket.kategori] || KATEGORI_ETIKETLER.diger;
                const dur = DURUM_ETIKETLER[ticket.durum] || DURUM_ETIKETLER.acik;
                return (
                  <button
                    key={ticket.id}
                    className={`list-group-item list-group-item-action d-flex align-items-center gap-3 px-4 py-3 ${isDark ? 'bg-dark text-white border-secondary' : ''}`}
                    onClick={() => navigate(`/destek/ticket/${ticket.id}`)}
                  >
                    <span className="material-symbols-outlined text-muted">{kat.icon}</span>
                    <div className="flex-grow-1 min-w-0">
                      <div className="fw-semibold text-truncate">{ticket.baslik}</div>
                      <div className="small text-muted">
                        {new Date(ticket.guncelleme_tarihi).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        &nbsp;·&nbsp;{ticket.mesaj_sayisi} mesaj
                      </div>
                    </div>
                    <div className="d-flex gap-2 flex-shrink-0">
                      <Badge bg={kat.renk} className="text-dark">{kat.label}</Badge>
                      <Badge bg={dur.renk}>{dur.label}</Badge>
                    </div>
                    <span className="material-symbols-outlined text-muted">chevron_right</span>
                  </button>
                );
              })}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Yeni Ticket Modal */}
      <Modal show={modalAcik} onHide={() => setModalAcik(false)} centered>
        <Form onSubmit={handleTicketGonder}>
          <Modal.Header closeButton className={isDark ? 'bg-dark text-white border-secondary' : ''}>
            <Modal.Title>
              <span className="material-symbols-outlined align-middle me-2">add_comment</span>
              Yeni Destek Talebi
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className={isDark ? 'bg-dark text-white' : ''}>
            {modalHata && <Alert variant="danger">{modalHata}</Alert>}
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Kategori</Form.Label>
              <Form.Select
                value={yeniTicket.kategori}
                onChange={e => setYeniTicket(p => ({ ...p, kategori: e.target.value }))}
                className={isDark ? 'bg-secondary text-white border-secondary' : ''}
                required
              >
                {Object.entries(KATEGORI_ETIKETLER).map(([key, val]) => (
                  <option key={key} value={key}>{val.label}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Başlık</Form.Label>
              <Form.Control
                type="text"
                placeholder="Talebinizi kısaca özetleyin..."
                value={yeniTicket.baslik}
                onChange={e => setYeniTicket(p => ({ ...p, baslik: e.target.value }))}
                maxLength={200}
                required
                className={isDark ? 'bg-secondary text-white border-secondary' : ''}
              />
            </Form.Group>
            <Form.Group className="mb-1">
              <Form.Label className="fw-semibold">Mesaj</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Detayları buraya yazın..."
                value={yeniTicket.ilk_mesaj}
                onChange={e => setYeniTicket(p => ({ ...p, ilk_mesaj: e.target.value }))}
                required
                className={isDark ? 'bg-secondary text-white border-secondary' : ''}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className={isDark ? 'bg-dark border-secondary' : ''}>
            <Button variant="secondary" onClick={() => setModalAcik(false)} disabled={gonderiyor}>
              İptal
            </Button>
            <Button variant="primary" type="submit" disabled={gonderiyor}>
              {gonderiyor ? <Spinner size="sm" animation="border" className="me-2" /> : null}
              Gönder
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}

export default DestekSayfasi;
